import { Center, HStack, Image } from "@chakra-ui/react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { Button } from "adminComponents/atoms/Button";
import { Card } from "adminComponents/atoms/Card";
import { Heading } from "adminComponents/atoms/Heading";
import { Text } from "adminComponents/atoms/Text";
import { useAgreementsText } from "adminComponents/hooks/useAgreementsText";
import { TemplateWithVerticalCenteredContent } from "adminComponents/templates/TemplateWithVerticalCenteredContent";
import { pxToRem } from "adminComponents/utils";
import { useShowToast } from "adminComponents/utils/toast";
import { useFetchRequiredAgreements } from "lib/features/agreements";
import { useAuth } from "lib/features/auth";
import { useUnfreezeUser } from "lib/features/users";
import { UserRole } from "lib/types";
import { HeaderWithLanguagePicker } from "sharedComponents/atoms/HeaderWithLanguagePicker";

import PEARY_CROSSING_GUARD from "./resources/peary-crossing-guard.png";

export const FrozenUser: React.FC = () => {
  const { t } = useTranslation("admin", {
    keyPrefix: "frozenUser",
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation("admin", {
    keyPrefix: "common",
    useSuspense: false,
  });
  const showToast = useShowToast();
  const { authUser, refetchUser, setAuthToken } = useAuth();
  const history = useHistory();

  const {
    data: requiredAgreements,
    isLoading: isFetchRequiredAgreementsLoading,
  } = useFetchRequiredAgreements({
    enabled: authUser?.role !== UserRole.Student,
  });
  const { mutate: unfreezeUser, isLoading: isUnfreezeUserLoading } =
    useUnfreezeUser({
      onSuccess: (data) => {
        const token = data?.token;
        if (token) {
          setAuthToken(token);
        }

        showToast(t("successToast"));
        refetchUser();
      },
      onError: () => {
        showToast(t("failureToast"));
      },
    });

  const acceptedAgreementIds = useMemo(() => {
    return (
      requiredAgreements?.agreements?.map((agreement) => agreement.id) ?? []
    );
  }, [requiredAgreements]);

  const agreementsText = useAgreementsText({
    agreements: requiredAgreements?.agreements ?? [],
    buttonLabel: tCommon("continue"),
  });

  return (
    <TemplateWithVerticalCenteredContent
      headerContent={<HeaderWithLanguagePicker showSignOut />}
    >
      <Card
        marginTop={pxToRem(12)}
        borderColor="primary.tan"
        backgroundColor="primary.white"
        variant="adminCardMediumBorder"
      >
        <Heading
          sx={{ fontSize: [pxToRem(32), null, pxToRem(48)] }}
          as="h1"
          variant="adminH3TTCommons"
        >
          {t("cta")}
        </Heading>
        <Center paddingY={pxToRem(12)}>
          <Image src={PEARY_CROSSING_GUARD} />
        </Center>
        <Text variant="adminH3TTCommons">{t("description")}</Text>
        <br></br>
        {authUser?.role !== UserRole.Student && agreementsText}
        <HStack w="full" justifyContent="flex-start" paddingTop={pxToRem(20)}>
          <Button
            size="md"
            variant="adminButtonFilled"
            isLoading={
              isFetchRequiredAgreementsLoading || isUnfreezeUserLoading
            }
            onClick={() => {
              unfreezeUser({ accepted_agreement_ids: acceptedAgreementIds });
            }}
          >
            {tCommon("continue")}
          </Button>
          <Button
            size="md"
            variant="adminButtonFilled"
            isLoading={
              isFetchRequiredAgreementsLoading || isUnfreezeUserLoading
            }
            onClick={() => {
              history.push("/sign-out");
            }}
          >
            {t("signInWithAnotherAccountButtonLabel")}
          </Button>
        </HStack>
      </Card>
    </TemplateWithVerticalCenteredContent>
  );
};
